import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * useCellSelection
 * Owns the cell-selection state machine: the active cell, the selection
 * rectangle (anchor + focus), and the selection "type" (cell / row / col).
 * Exposes click handlers for cells, row headers, and column headers along
 * with Shift / Ctrl modifier semantics that match Excel's behaviour.
 *
 * PER-SHEET, BLINK-FREE: every sheet's selection lives in ONE map keyed by
 * sheet name, and the current selection is DERIVED from it for the active
 * sheet.  Because it's derived (not restored via an effect), switching tabs
 * shows the right cursor/selection in the very same render — the new sheet
 * never flashes the previous sheet's cursor first.  Returning to a sheet
 * restores its full cursor + row/range selection exactly as you left it.
 */
const DEFAULT_SEL = Object.freeze({
  selectedCell: { row: 0, col: 0 },
  selection: { anchor: { row: 0, col: 0 }, focus: { row: 0, col: 0 }, dragging: false },
  selectionType: "cell",
});

export default function useCellSelection({ activeSheet, normalizedRows, maxCols }) {
  const [bySheet, setBySheet] = useState({});
  const current = bySheet[activeSheet] || DEFAULT_SEL;
  const { selectedCell, selection, selectionType } = current;

  // Live active sheet read inside the (stable) setters below.
  const activeSheetRef = useRef(activeSheet);
  activeSheetRef.current = activeSheet;

  // Merge a delta into the ACTIVE sheet's selection slice.  Stable identity
  // (reads the sheet from a ref) so the handlers never go stale.
  const patch = useCallback((updater) => {
    const sheet = activeSheetRef.current;
    setBySheet((prev) => {
      const cur = prev[sheet] || DEFAULT_SEL;
      const delta = typeof updater === "function" ? updater(cur) : updater;
      return { ...prev, [sheet]: { ...cur, ...delta } };
    });
  }, []);

  const setSelectedCell = useCallback(
    (v) => patch((cur) => ({ selectedCell: typeof v === "function" ? v(cur.selectedCell) : v })),
    [patch],
  );
  const setSelection = useCallback(
    (v) => patch((cur) => ({ selection: typeof v === "function" ? v(cur.selection) : v })),
    [patch],
  );
  const setSelectionType = useCallback(
    (v) => patch((cur) => ({ selectionType: typeof v === "function" ? v(cur.selectionType) : v })),
    [patch],
  );

  // End drag-select on mouseup anywhere on the document.
  useEffect(() => {
    if (!selection.dragging) return undefined;
    const onMouseUp = () => setSelection((prev) => ({ ...prev, dragging: false }));
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, [selection.dragging, setSelection]);

  const normalizedSelection = useMemo(() => {
    const r1 = Math.min(selection.anchor.row, selection.focus.row);
    const r2 = Math.max(selection.anchor.row, selection.focus.row);
    const c1 = Math.min(selection.anchor.col, selection.focus.col);
    const c2 = Math.max(selection.anchor.col, selection.focus.col);
    return { r1, r2, c1, c2 };
  }, [selection.anchor, selection.focus]);

  const startSelection = useCallback((row, col, event) => {
    const isShift = event.shiftKey;
    const isToggle = event.ctrlKey || event.metaKey;
    setSelectionType("cell");

    if (isShift) {
      setSelection((prev) => ({ anchor: prev.anchor, focus: { row, col }, dragging: true }));
    } else {
      setSelection({ anchor: { row, col }, focus: { row, col }, dragging: !isToggle });
    }
    setSelectedCell({ row, col });
  }, [setSelection, setSelectedCell, setSelectionType]);

  const extendSelection = useCallback((row, col) => {
    setSelection((prev) => (prev.dragging ? { ...prev, focus: { row, col } } : prev));
  }, [setSelection]);

  // ── Column selection ──────────────────────────────────────────────
  // Plain mousedown starts a DRAG-capable column selection (dragging:true);
  // dragging across other column headers extends it via
  // extendColumnSelection.  Shift-click extends from the existing anchor.
  const selectColumn = useCallback((colIndex, event) => {
    const totalRows = normalizedRows.length;
    if (event && event.preventDefault) event.preventDefault(); // no text selection while dragging

    if (event && event.shiftKey && selectionType === "col") {
      setSelection((prev) => ({ anchor: prev.anchor, focus: { row: totalRows - 1, col: colIndex }, dragging: false }));
    } else {
      setSelection({ anchor: { row: 0, col: colIndex }, focus: { row: totalRows - 1, col: colIndex }, dragging: true });
    }
    setSelectionType("col");
    setSelectedCell({ row: 0, col: colIndex });
  }, [normalizedRows.length, selectionType, setSelection, setSelectionType, setSelectedCell]);

  const extendColumnSelection = useCallback((colIndex) => {
    if (selectionType !== "col") return;
    setSelection((prev) => (prev.dragging ? { ...prev, focus: { row: normalizedRows.length - 1, col: colIndex } } : prev));
  }, [selectionType, normalizedRows.length, setSelection]);

  // ── Row selection (mirror of columns) ─────────────────────────────
  const selectRow = useCallback((rowIndex, event) => {
    if (event && event.preventDefault) event.preventDefault();

    if (event && event.shiftKey && selectionType === "row") {
      setSelection((prev) => ({ anchor: prev.anchor, focus: { row: rowIndex, col: maxCols - 1 }, dragging: false }));
    } else {
      setSelection({ anchor: { row: rowIndex, col: 0 }, focus: { row: rowIndex, col: maxCols - 1 }, dragging: true });
    }
    setSelectionType("row");
    setSelectedCell({ row: rowIndex, col: 0 });
  }, [maxCols, selectionType, setSelection, setSelectionType, setSelectedCell]);

  const extendRowSelection = useCallback((rowIndex) => {
    if (selectionType !== "row") return;
    setSelection((prev) => (prev.dragging ? { ...prev, focus: { row: rowIndex, col: maxCols - 1 } } : prev));
  }, [selectionType, maxCols, setSelection]);

  return {
    selectedCell, setSelectedCell,
    selection, setSelection,
    selectionType, setSelectionType,
    normalizedSelection,
    startSelection, extendSelection,
    selectColumn, selectRow,
    extendColumnSelection, extendRowSelection,
  };
}
